import { useEffect, useState } from 'react';
import { api, otApi } from '../api';

// Matches kommercio-admin backend /api/dashboard response shape.
type DashboardStats = {
  totalCustomers?: number;
  activeSubscriptions?: number;
  mrr?: number;
  churnRate?: number;
  revenueByProduct?: Array<{ id: number; name: string; subscribers: string; revenue: string }>;
  [k: string]: unknown;
};

// Pakk health from OnlyTrade /api/admin/health/pakk
type PakkHealth = {
  status: 'green' | 'yellow' | 'red';
  reasons: string[];
  pakk: {
    success_rate_1h: number | null;
    success_rate_24h: number | null;
    sample_size_1h: number;
    sample_size_24h: number;
    last_failure: { timestamp: string; oem: string | null; error: string | null } | null;
  };
  snapshot: { age_hours: number | null; generated_at: string | null };
  cron: { last_run_at: string | null; hours_since: number | null; last_status: string };
  last_alert_sent_at: string | null;
};
type PakkRecentCall = { timestamp: string; success: boolean; oem: string | null; error: string | null };

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pakk, setPakk] = useState<PakkHealth | null>(null);
  const [pakkErr, setPakkErr] = useState<string | null>(null);
  const [pakkRecent, setPakkRecent] = useState<PakkRecentCall[] | null>(null);
  const [showCalls, setShowCalls] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api<DashboardStats>('/dashboard');
        if (!cancelled) setStats(data || {});
      } catch (e) { if (!cancelled) setError((e as Error).message); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pakk health — poll every 30s while the dashboard is open
  useEffect(() => {
    let cancelled = false;
    async function fetchHealth() {
      try {
        const h = await otApi<PakkHealth>('/admin/health/pakk');
        if (!cancelled) { setPakk(h); setPakkErr(null); }
      } catch (e) { if (!cancelled) setPakkErr((e as Error).message); }
    }
    fetchHealth();
    const t = setInterval(fetchHealth, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  async function loadRecentCalls() {
    setShowCalls(true);
    try {
      const r = await otApi<{ calls: PakkRecentCall[] }>('/admin/health/pakk/recent?limit=50');
      setPakkRecent(r.calls || []);
    } catch (e) { setPakkRecent([{ timestamp: '', success: false, oem: null, error: (e as Error).message }]); }
  }

  const mrr = stats?.mrr;
  const arr = mrr != null ? mrr * 12 : undefined;
  const topProducts = stats?.revenueByProduct || [];
  const totalRevenue = topProducts.reduce((s, p) => s + parseFloat(p.revenue || '0'), 0);
  const cards: { label: string; value: string | number; tone?: 'brand' | 'success' | 'warn' }[] = [
    { label: 'MRR',                value: mrr != null ? `£${Number(mrr).toLocaleString('en-GB', {maximumFractionDigits: 0})}` : '—', tone: 'brand' },
    { label: 'ARR',                value: arr != null ? `£${Number(arr).toLocaleString('en-GB', {maximumFractionDigits: 0})}` : '—' },
    { label: 'Active subs',        value: String(stats?.activeSubscriptions ?? '—') },
    { label: 'Total customers',    value: String(stats?.totalCustomers ?? '—') },
    { label: 'Churn rate',         value: stats?.churnRate != null ? `${Number(stats.churnRate).toFixed(1)}%` : '—', tone: 'warn' },
    { label: 'Revenue total',      value: `£${totalRevenue.toLocaleString('en-GB', {maximumFractionDigits: 0})}` },
    { label: 'Products live',      value: String(topProducts.length || '—') },
    { label: 'Auth',               value: 'Passkey + PW', tone: 'success' },
  ];

  // Status pill — colour + emoji + condensed reason
  const pillFor = (h: PakkHealth | null): { dot: string; bg: string; border: string; text: string; label: string } => {
    if (!h) return { dot: 'bg-gray-500', bg: 'bg-gray-900/40', border: 'border-gray-700', text: 'text-gray-300', label: 'Pakk: —' };
    const map = {
      green:  { dot: 'bg-green-500',  bg: 'bg-green-950/40',  border: 'border-green-800',  text: 'text-green-300',  label: 'Pakk: Healthy' },
      yellow: { dot: 'bg-amber-500',  bg: 'bg-amber-950/40',  border: 'border-amber-800',  text: 'text-amber-300',  label: 'Pakk: Degraded' },
      red:    { dot: 'bg-red-500',    bg: 'bg-red-950/40',    border: 'border-red-800',    text: 'text-red-300',    label: 'Pakk: Failing' },
    } as const;
    return map[h.status];
  };
  const pill = pillFor(pakk);
  const pct = (n: number | null) => n == null ? 'n/a' : `${Math.round(n * 100)}%`;
  const fmtAge = (h: number | null) => h == null ? 'unknown' : h < 1 ? `${Math.round(h * 60)}m` : h < 24 ? `${Math.round(h)}h` : `${Math.round(h / 24)}d`;

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3 flex-wrap">
        <h1 className="text-xl sm:text-2xl font-semibold">Dashboard</h1>
        {/* Pakk health pill — always visible top-right of header. Click to expand call log. */}
        <button
          type="button"
          onClick={loadRecentCalls}
          title={pakk ? (pakk.reasons.length ? pakk.reasons.join(' · ') : 'All systems nominal') : 'Loading…'}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${pill.bg} ${pill.border} ${pill.text} hover:brightness-110`}
        >
          <span className={`inline-block w-2 h-2 rounded-full ${pill.dot} ${pakk?.status === 'red' ? 'animate-pulse' : ''}`} />
          {pill.label}
          {pakk && pakk.pakk.success_rate_1h !== null && (
            <span className="opacity-75 font-normal">· {pct(pakk.pakk.success_rate_1h)} 1h</span>
          )}
        </button>
      </div>

      {error && (
        <div className="bg-red-950/50 border border-red-900 text-red-300 rounded p-3 text-sm mb-4">
          Failed to load dashboard: {error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
        {cards.map(c => (
          <div key={c.label} className="bg-ink-card border border-ink-border rounded-lg p-3 sm:p-5 hover:border-brand-600 transition-colors">
            <div className="text-[10px] sm:text-[11px] uppercase text-ink-muted font-semibold tracking-wider mb-1 sm:mb-2">{c.label}</div>
            <div className={`text-lg sm:text-2xl font-bold tabular-nums break-words ${
              c.tone === 'brand' ? 'text-brand-400' :
              c.tone === 'success' ? 'text-green-400' :
              c.tone === 'warn' ? 'text-amber-400' : 'text-ink-text'
            }`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* ─── Pakk health detail panel ─── */}
      <div className="mt-8 bg-ink-card border border-ink-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-sm font-semibold">Pakk live-stock integration</div>
          {pakk && pakk.last_alert_sent_at && (
            <div className="text-[11px] text-ink-muted">Last alert email: {new Date(pakk.last_alert_sent_at).toLocaleString('en-GB')}</div>
          )}
        </div>
        {pakkErr && (
          <div className="text-xs text-red-300 mb-3">Could not load health: {pakkErr}</div>
        )}
        {pakk && (
          <>
            {pakk.reasons.length > 0 && (
              <ul className={`text-xs mb-3 list-disc pl-5 ${pakk.status === 'red' ? 'text-red-300' : 'text-amber-300'}`}>
                {pakk.reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-ink-bg border border-ink-border rounded p-3">
                <div className="text-[10px] uppercase text-ink-muted font-semibold tracking-wider mb-1">Success 1h</div>
                <div className="text-lg font-bold tabular-nums">{pct(pakk.pakk.success_rate_1h)}</div>
                <div className="text-[10px] text-ink-muted">{pakk.pakk.sample_size_1h} calls</div>
              </div>
              <div className="bg-ink-bg border border-ink-border rounded p-3">
                <div className="text-[10px] uppercase text-ink-muted font-semibold tracking-wider mb-1">Success 24h</div>
                <div className="text-lg font-bold tabular-nums">{pct(pakk.pakk.success_rate_24h)}</div>
                <div className="text-[10px] text-ink-muted">{pakk.pakk.sample_size_24h} calls</div>
              </div>
              <div className="bg-ink-bg border border-ink-border rounded p-3">
                <div className="text-[10px] uppercase text-ink-muted font-semibold tracking-wider mb-1">Snapshot age</div>
                <div className="text-lg font-bold tabular-nums">{fmtAge(pakk.snapshot.age_hours)}</div>
                <div className="text-[10px] text-ink-muted">{pakk.snapshot.generated_at ? new Date(pakk.snapshot.generated_at).toLocaleDateString('en-GB') : '—'}</div>
              </div>
              <div className="bg-ink-bg border border-ink-border rounded p-3">
                <div className="text-[10px] uppercase text-ink-muted font-semibold tracking-wider mb-1">Cron last run</div>
                <div className="text-lg font-bold tabular-nums">{pakk.cron.hours_since != null ? fmtAge(pakk.cron.hours_since) + ' ago' : 'never'}</div>
                <div className="text-[10px] text-ink-muted">{pakk.cron.last_status}</div>
              </div>
            </div>
            {pakk.pakk.last_failure && (
              <div className="mt-3 text-xs text-ink-muted">
                Last failure {new Date(pakk.pakk.last_failure.timestamp).toLocaleString('en-GB')}: <span className="text-red-300">{pakk.pakk.last_failure.error}</span> {pakk.pakk.last_failure.oem ? `(OEM ${pakk.pakk.last_failure.oem})` : ''}
              </div>
            )}
            {!showCalls && (
              <button type="button" onClick={loadRecentCalls} className="mt-3 text-xs text-brand-400 hover:text-brand-300 underline">Show last 50 Pakk calls</button>
            )}
            {showCalls && pakkRecent && (
              <div className="mt-3 max-h-64 overflow-y-auto border border-ink-border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-ink-bg sticky top-0">
                    <tr className="text-ink-muted text-left">
                      <th className="px-2 py-1 font-semibold">Time</th>
                      <th className="px-2 py-1 font-semibold">OEM</th>
                      <th className="px-2 py-1 font-semibold">Status</th>
                      <th className="px-2 py-1 font-semibold">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pakkRecent.length === 0 && (
                      <tr><td colSpan={4} className="px-2 py-3 text-center text-ink-muted">No calls recorded yet</td></tr>
                    )}
                    {pakkRecent.map((c, i) => (
                      <tr key={i} className="border-t border-ink-border">
                        <td className="px-2 py-1 tabular-nums whitespace-nowrap">{c.timestamp ? new Date(c.timestamp).toLocaleTimeString('en-GB') : '—'}</td>
                        <td className="px-2 py-1 font-mono">{c.oem || '—'}</td>
                        <td className={`px-2 py-1 font-semibold ${c.success ? 'text-green-400' : 'text-red-400'}`}>{c.success ? 'OK' : 'FAIL'}</td>
                        <td className="px-2 py-1 text-ink-muted">{c.error || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <div className="mt-6 bg-ink-card border border-ink-border rounded-lg p-5">
        <div className="text-sm font-semibold mb-2">Quick links</div>
        <div className="flex flex-wrap gap-2 text-sm">
          <a href="/products" className="px-3 py-1.5 bg-ink-bg border border-ink-border rounded hover:border-brand-600">Products</a>
          <a href="/subscriptions" className="px-3 py-1.5 bg-ink-bg border border-ink-border rounded hover:border-brand-600">Subscriptions</a>
          <a href="/customers" className="px-3 py-1.5 bg-ink-bg border border-ink-border rounded hover:border-brand-600">Customers</a>
          <a href="/jobs" className="px-3 py-1.5 bg-ink-bg border border-ink-border rounded hover:border-brand-600">Jobs</a>
          <a href="/quotes" className="px-3 py-1.5 bg-ink-bg border border-ink-border rounded hover:border-brand-600">Quotes</a>
        </div>
      </div>
    </div>
  );
}
